import { format, parseISO, isValid, formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';

// 📅 FORMATEADORES DE FECHA
export const formatters = {
  
  // Formatear fecha a formato chileno (dd/mm/yyyy)
  date: (date) => {
    if (!date) return '-';
    
    try {
      const dateObj = typeof date === 'string' ? parseISO(date) : date;
      if (!isValid(dateObj)) return '-';
      
      return format(dateObj, 'dd/MM/yyyy', { locale: es });
    } catch (error) {
      console.error('Error formatting date:', error);
      return '-';
    }
  },

  // Formatear fecha y hora
  datetime: (date) => {
    if (!date) return '-';
    
    try {
      const dateObj = typeof date === 'string' ? parseISO(date) : date;
      if (!isValid(dateObj)) return '-';
      
      return format(dateObj, 'dd/MM/yyyy HH:mm', { locale: es });
    } catch (error) {
      console.error('Error formatting datetime:', error);
      return '-';
    }
  },

  // Formatear solo hora
  time: (date) => {
    if (!date) return '-';
    
    try {
      const dateObj = typeof date === 'string' ? parseISO(date) : date;
      if (!isValid(dateObj)) return '-';
      
      return format(dateObj, 'HH:mm', { locale: es });
    } catch (error) {
      console.error('Error formatting time:', error);
      return '-';
    }
  },

  // Formatear fecha relativa (hace 2 días, hace 1 hora)
  dateRelative: (date) => {
    if (!date) return '-';
    
    try {
      const dateObj = typeof date === 'string' ? parseISO(date) : date;
      if (!isValid(dateObj)) return '-';
      
      return formatDistanceToNow(dateObj, { 
        addSuffix: true, 
        locale: es 
      });
    } catch (error) {
      console.error('Error formatting relative date:', error);
      return '-';
    }
  },

  // Formatear mes y año
  monthYear: (date) => {
    if (!date) return '-';
    
    try {
      const dateObj = typeof date === 'string' ? parseISO(date) : date;
      if (!isValid(dateObj)) return '-';
      
      return format(dateObj, 'MMMM yyyy', { locale: es });
    } catch (error) {
      console.error('Error formatting month/year:', error);
      return '-';
    }
  },

  // 💰 FORMATEADORES DE MONEDA
  
  // Formatear moneda chilena
  currency: (amount, options = {}) => {
    if (amount === null || amount === undefined) return '-';
    
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(numAmount)) return '-';
    
    try {
      return new Intl.NumberFormat('es-CL', {
        style: 'currency',
        currency: 'CLP',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
        ...options
      }).format(numAmount);
    } catch (error) {
      console.error('Error formatting currency:', error);
      return `$${numAmount.toLocaleString('es-CL')}`;
    }
  },

  // Formatear número sin símbolo de moneda
  number: (num, options = {}) => {
    if (num === null || num === undefined) return '-';
    
    const numValue = typeof num === 'string' ? parseFloat(num) : num;
    if (isNaN(numValue)) return '-';
    
    try {
      return new Intl.NumberFormat('es-CL', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
        ...options
      }).format(numValue);
    } catch (error) {
      console.error('Error formatting number:', error);
      return numValue.toString();
    }
  },

  // Formatear porcentaje
  percentage: (value, options = {}) => {
    if (value === null || value === undefined) return '-';
    
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue)) return '-';
    
    try {
      return new Intl.NumberFormat('es-CL', {
        style: 'percent',
        minimumFractionDigits: 0,
        maximumFractionDigits: 1,
        ...options
      }).format(numValue / 100);
    } catch (error) {
      console.error('Error formatting percentage:', error);
      return `${numValue}%`;
    }
  },

  // 📱 FORMATEADORES DE TEXTO
  
  // Capitalizar primera letra
  capitalize: (str) => {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  },

  // Formatear nombre completo
  fullName: (firstName, lastName) => {
    const first = firstName?.trim() || '';
    const last = lastName?.trim() || '';
    
    if (!first && !last) return '-';
    return `${first} ${last}`.trim();
  },

  // Formatear teléfono chileno
  phone: (phone) => {
    if (!phone) return '-';
    
    // Remover caracteres no numéricos
    const cleaned = phone.replace(/\D/g, '');
    
    // Formato chileno: +56 9 xxxx xxxx
    if (cleaned.length === 11 && cleaned.startsWith('569')) {
      return `+${cleaned.slice(0, 2)} ${cleaned.slice(2, 3)} ${cleaned.slice(3, 7)} ${cleaned.slice(7)}`;
    }
    
    // Formato local: 9 xxxx xxxx
    if (cleaned.length === 9 && cleaned.startsWith('9')) {
      return `${cleaned.slice(0, 1)} ${cleaned.slice(1, 5)} ${cleaned.slice(5)}`;
    }
    
    return phone; // Devolver original si no coincide con formato
  },

  // Truncar texto
  truncate: (text, maxLength = 50) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    
    return text.substring(0, maxLength).trim() + '...';
  },

  // 📊 FORMATEADORES DE DATOS
  
  // Formatear tamaño de archivo
  fileSize: (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
  },

  // Formatear duración (en minutos)
  duration: (minutes) => {
    if (!minutes || minutes === 0) return '0 min';
    
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    
    if (hours === 0) {
      return `${remainingMinutes} min`;
    }
    
    if (remainingMinutes === 0) {
      return `${hours}h`;
    }
    
    return `${hours}h ${remainingMinutes}min`;
  },

  // Formatear estado con color
  status: (status) => {
    const statusMap = {
      'activa': { text: 'Activa', color: 'blue' },
      'activo': { text: 'Activo', color: 'blue' },
      'cancelada': { text: 'Cancelada', color: 'error' },
      'cancelado': { text: 'Cancelado', color: 'error' },
      'inactivo': { text: 'Inactivo', color: 'warning' },
      'pausado': { text: 'Pausado', color: 'warning' },
      'vencida': { text: 'Vencida', color: 'error' },
      'completado': { text: 'Completado', color: 'blue' },
      'pendiente': { text: 'Pendiente', color: 'warning' },
      'finalizado': { text: 'Finalizado', color: 'blue' },
    };
    
    return statusMap[status?.toLowerCase()] || { text: status || '-', color: 'default' };
  }
};

// 🎨 UTILIDADES ADICIONALES
/**
 * Limpia un nombre para mostrarlo: saca espacios de los extremos y colapsa los
 * repetidos del medio.
 *
 * Los nombres se guardaron tal como los tipeó el socio, y muchos traen un espacio
 * al final ("fabian suarez "). En pantalla eso se ve como un hueco raro y, peor,
 * al concatenarlo ("fabian suarez  (26134202-2)") aparece un doble espacio.
 *
 * OJO: es solo para la vista. El cruce contra los documentos de Bsale se hace por
 * nombre, así que normalizar el dato guardado cambiaría a qué calza — eso se
 * normaliza aparte, en la comparación.
 */
export const limpiarNombre = (s) => String(s ?? '').replace(/\s+/g, ' ').trim();

export const formatUtils = {

  // Formatear array a lista legible
  arrayToText: (arr, separator = ', ') => {
    if (!Array.isArray(arr) || arr.length === 0) return '-';
    return arr.join(separator);
  },

  // Formatear horarios
  schedule: (horarios) => {
    if (!Array.isArray(horarios) || horarios.length === 0) return '-';
    
    return horarios.map(horario => {
      const day = formatters.capitalize(horario.dia);
      const startTime = formatters.time(horario.hora_inicio);
      const endTime = formatters.time(horario.hora_fin);
      
      return `${day} ${startTime} - ${endTime}`;
    }).join(', ');
  },

  // Formatear categoría
  category: (categoria) => {
    const categoryMap = {
      'gastronomia': 'Gastronomía',
      'deportes': 'Deportes',
      'artes': 'Artes',
      'tecnologia': 'Tecnología',
      'idiomas': 'Idiomas',
      'bienestar': 'Bienestar',
    };
    
    return categoryMap[categoria?.toLowerCase()] || formatters.capitalize(categoria) || '-';
  },

  // Formatear rol de usuario
  userRole: (role) => {
    const roleMap = {
      'admin': 'Administrador',
      'supervisor': 'Supervisor',
      'cliente': 'Cliente',
    };
    
    return roleMap[role?.toLowerCase()] || formatters.capitalize(role) || '-';
  },

  // Formatear tipo de descuento
  discountType: (tipo) => {
    const typeMap = {
      'porcentaje': 'Porcentaje',
      'monto_fijo': 'Monto Fijo',
      'tarjeta_vecino': 'Tarjeta Vecino',
    };
    
    return typeMap[tipo?.toLowerCase()] || formatters.capitalize(tipo) || '-';
  },

  // Formatear método de pago
  paymentMethod: (metodo) => {
    const methodMap = {
      'tarjeta': 'Tarjeta de Crédito/Débito',
      'transferencia': 'Transferencia Bancaria',
      'efectivo': 'Efectivo',
      'paypal': 'PayPal',
    };
    
    return methodMap[metodo?.toLowerCase()] || formatters.capitalize(metodo) || '-';
  },
  
  capitalize: (str) => {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

  },

  abbrDia: (dia) => {
    // Normaliza nombres de días a iniciales: "LUNES"=> "L"
    if (!dia) return "";
    const map = {
      LUNES: "L",
      MARTES: "M",
      MIÉRCOLES: "M",
      MIERCOLES: "M",
      JUEVES: "J",
      VIERNES: "V",
      SÁBADO: "S",
      SABADO: "S",
      DOMINGO: "D",
    };
    const nombre = (typeof dia === "string" ? dia : "").toUpperCase();
    return map[nombre] || nombre.slice(0, 1) || "";
  },

  edadRango: (cat) => {
    // Ejemplo: "ADULTO" => "18 a 65" | "TODAS_LAS_EDADES" => "Todas las edades"
    if (!cat) return "";
    if (cat === "TODAS_LAS_EDADES") return "Todas las edades";
    if (cat === "ADULTO") return "18 a 65";
    if (cat.toUpperCase().includes("INFANTIL")) return "6 a 12";
    if (cat.toUpperCase().includes("JOVEN")) return "13 a 17";
    return formatters.capitalize(cat.replaceAll("_", " "));
  },

  fechaCorta: (fecha) => {
    // Input: fecha en ISO/string/Date | Output: "7 de octubre" (en español)
    if (!fecha) return "-";
    try {
      const date = typeof fecha === "string" ? new Date(fecha) : fecha;
      return `${date.getDate()} de ${[
        "enero",
        "febrero",
        "marzo",
        "abril",
        "mayo",
        "junio",
        "julio",
        "agosto",
        "septiembre",
        "octubre",
        "noviembre",
        "diciembre",
      ][date.getMonth()]}`;
    } catch {
      return "-";
    }
  },
};

// Exportar formateador principal
export default formatters;
